/**
 * Pre-rendered speech playback.
 *
 * The locked plan is emphatic that voice is *authored*, never generated at runtime: Deshret
 * renders every NPC line with Fish Audio and the engine plays the files. This is the whole of the
 * engine's side of that bargain — fetch a clip, decode it, play it on the voice bus.
 *
 * ## Missing audio is not an error
 *
 * A conversation is playable as text; the clip is the performance of a line the player can
 * already read. So a 404, a decode failure, a codec the browser will not take, or an author who
 * has simply not recorded that node yet all resolve to the same thing: the line is on screen and
 * nothing is said aloud. Nothing is surfaced to the player and nothing blocks the tree — the same
 * rule Chapter 4 applied to speech recognition, for the same reason.
 *
 * Buffers are cached by URL because a player who walks the same branch twice should not re-fetch,
 * and a conversation is a handful of short files.
 */
import type { Mixer } from './mixer.js';
import { audioContext } from './context.js';
import { createRadioVoice, type RadioVoice } from './radioVoice.js';

export interface SpeechPlayerOptions {
  mixer: Mixer;
  /** 0 = full radio character, 1 = plainest. See `radioVoice.ts`. */
  clarity?: number;
}

export class SpeechPlayer {
  private readonly mixer: Mixer;
  private readonly cache = new Map<string, AudioBuffer | null>();
  private current: AudioBufferSourceNode | null = null;
  /** Bumped on every play and stop, so a slow fetch cannot start over a newer line. */
  private generation = 0;
  /**
   * The radio chain, built on first use rather than in the constructor.
   *
   * A `SpeechPlayer` is constructed while the page is still assembling and there may be no
   * running audio context yet; building six nodes then would either throw or create a context
   * before the player has touched anything, which browsers rightly dislike.
   */
  private voice: RadioVoice | null = null;
  private clarity: number;

  constructor(options: SpeechPlayerOptions) {
    this.mixer = options.mixer;
    this.clarity = options.clarity ?? 0;
  }

  /** Move the clarity slider. Takes effect on the next word, and on everything after. */
  setClarity(value: number): void {
    this.clarity = value;
    this.voice?.setClarity(value);
  }

  /**
   * Play a clip, replacing whatever was playing.
   *
   * Returns whether audio actually started, which is for tests and diagnostics — no caller should
   * change what the player sees based on it.
   */
  async play(url: string, opts?: { dry?: boolean }): Promise<boolean> {
    this.stop();
    const generation = this.generation;

    let buffer = this.cache.get(url);
    if (buffer === undefined) {
      buffer = await this.load(url);
      this.cache.set(url, buffer);
    }
    // A null entry is a remembered failure: do not fetch it again every time the node is entered.
    if (!buffer) return false;
    // The line moved on while this was loading. Playing now would talk over it.
    if (generation !== this.generation) return false;

    try {
      const source = audioContext().createBufferSource();
      source.buffer = buffer;
      // A dry clip skips the RF radio chain and goes straight to the voice bus. NPC lines are
      // rendered clean and *want* the radio character added here; a narrator's epilogue is not a
      // thing on the air, so it plays in the clear.
      source.connect(opts?.dry ? this.mixer.bus('voice') : this.destination());
      source.onended = () => {
        if (this.current === source) this.current = null;
        // The radio chain lives for the session, so a finished clip left connected to it stays
        // reachable from the destination and can never be collected — one leaked node per line
        // spoken.
        source.disconnect();
      };
      source.start();
      this.current = source;
      return true;
    } catch {
      // No usable audio context. The line is on screen; that is enough.
      return false;
    }
  }

  /** Stop whatever is playing. Safe to call when nothing is. */
  stop(): void {
    this.generation += 1;
    const source = this.current;
    this.current = null;
    if (!source) return;
    try {
      source.stop();
    } catch {
      // Already ended: `stop()` on a finished source throws in some engines and means nothing.
    }
  }

  /**
   * Where a clip is played into: the radio chain if one can be built, the bus directly if not.
   *
   * Falling back to the bare bus matters — a browser that chokes on `createWaveShaper` should
   * cost the player some texture, not the line itself.
   */
  private destination(): AudioNode {
    if (!this.voice) {
      try {
        this.voice = createRadioVoice(this.clarity);
        this.voice.output.connect(this.mixer.bus('voice'));
      } catch {
        return this.mixer.bus('voice');
      }
    }
    return this.voice.input;
  }

  private async load(url: string): Promise<AudioBuffer | null> {
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      return await audioContext().decodeAudioData(await response.arrayBuffer());
    } catch {
      // Network, CORS, a truncated file, a codec this browser will not take: all the same answer.
      return null;
    }
  }
}
